/**
 * Tier-4 specialisations (M21, docs/TOWERS_V2.md "Tier 4"): two branches per tower type, each a tier-4 stat line (its
 * identity) and two skills. Loaded from `balance/src/branches.json` (via gen.py → balance.json → `branches`).
 *
 * A branch's numbers are named parameters (`tier4` for the branch, `params` for a skill) read by the mechanic that owns
 * them (sim/branches/*). Absent parameters read as zero, so a mechanic's defaults live with the mechanic, not here.
 */
import { Fix64 } from "../maths/fix64";

/** Named Fix64 parameters, read with `num` / `int`. */
export class BranchParams {
  private readonly values: ReadonlyMap<string, Fix64>;
  constructor(values: ReadonlyMap<string, Fix64>) { this.values = values; }
  static empty(): BranchParams { return new BranchParams(new Map()); }
  /** The parameter, or `fallback` when the data has none. */
  num(name: string, fallback: Fix64 = Fix64.Zero): Fix64 { return this.values.get(name) ?? fallback; }
  /** The parameter as a whole number (floored), or `fallback`. */
  int(name: string, fallback = 0): number { const v = this.values.get(name); return v === undefined ? fallback : v.floorToInt(); }
  has(name: string): boolean { return this.values.has(name); }
  /** Every parameter by name (sorted), for the info sheet and tests. */
  get names(): readonly string[] { return [...this.values.keys()].sort(); }
}

export interface SkillDef {
  /** Global index: branch index × 2 + slot (0 = skill A, 1 = skill B). */
  readonly index: number;
  readonly id: string;
  /** 0 = A, 1 = B. */
  readonly slot: number;
  readonly branchIndex: number;
  readonly name: string;
  readonly description: string;
  readonly params: BranchParams;
}

export interface BranchDef {
  /** Global index: tower position × 2 + slot, over the non-base towers in towers.json order. */
  readonly index: number;
  readonly id: string;
  /** The tower archetype index it belongs to. */
  readonly towerIndex: number;
  readonly towerId: string;
  /** 0 or 1: the branch's place among its tower's two (the picker's left and right card). */
  readonly slot: number;
  readonly name: string;
  /** The one-line identity the picker and the info sheet show. */
  readonly identity: string;
  /** The tier-4 line: damageMult, rangeBonus, fireIntervalMult (default 1), soldierArmour, and the mechanic's own numbers. */
  readonly tier4: BranchParams;
  readonly skills: readonly [SkillDef, SkillDef];
}

/** When the special card (Ascend or a skill) may ride an in-run offer (DESIGN §4.4, docs/TOWERS_V2.md "Tier 4"). */
export interface AscendPacing {
  /** Never on a level below this (1–3 are the tutorials). */
  readonly minLevel: number;
  /** Not before the stage this percent of the way through the level's stages (rounded up, at least stage 2). */
  readonly fromStagePercent: number;
  /** At most once every this many in-run offers that show; it stays due until one is taken. */
  readonly offerInterval: number;
  /** Endless (no stage count to take a percent of): not before this wave. Iron (one wave): from its first stage. */
  readonly endlessFromWave: number;
}

export interface BranchesData {
  readonly pacing: AscendPacing;
  readonly branches: readonly BranchDef[];
  readonly skills: readonly SkillDef[];
  /** Per tower archetype index: its two branch indexes, or [] (the base, or a bundle without branches). */
  readonly byTower: readonly (readonly number[])[];
}

/** No branches (an old bundle, a test fixture): no Ascend card is ever dealt. */
export function emptyBranches(towerCount = 0): BranchesData {
  return { pacing: { minLevel: Number.MAX_SAFE_INTEGER, fromStagePercent: 100, offerInterval: 1, endlessFromWave: Number.MAX_SAFE_INTEGER }, branches: [], skills: [], byTower: Array.from({ length: towerCount }, () => []) };
}

/** The branch a card id names, e.g. `ascend.archer` → the tower, `skill.archer.ranger.a` → the skill (null: no such id). */
export function skillById(data: BranchesData, cardId: string): SkillDef | null {
  const m = /^skill\.([a-z]+)\.([a-z]+)\.([ab])$/.exec(cardId);
  if (m === null) return null;
  const b = data.branches.find((x) => x.towerId === m[1] && x.id === m[2]);
  return b === undefined ? null : b.skills[m[3] === "a" ? 0 : 1];
}

/** The card id of an Ascend card for this tower id (`ascend.<tower>`). */
export function ascendCardId(towerId: string): string { return `ascend.${towerId}`; }
/** The card id of a skill card (`skill.<tower>.<branch>.<a|b>`). */
export function skillCardId(data: BranchesData, skill: SkillDef): string {
  const b = data.branches[skill.branchIndex];
  return `skill.${b.towerId}.${b.id}.${skill.slot === 0 ? "a" : "b"}`;
}
/** The view key of a tier-4 tower (`<tower>_t4_<branch>`): its model, icon and card art name. */
export function branchViewKey(b: BranchDef): string { return `${b.towerId}_t4_${b.id}`; }

type Json = any;

function params(v: Json): BranchParams {
  const m = new Map<string, Fix64>();
  if (v !== null && typeof v === "object") for (const [k, x] of Object.entries(v)) if (!k.startsWith("_")) m.set(k, Fix64.parse(String(x)));
  return new BranchParams(m);
}

/** Parses `branches` from the bundle; `towerIds` are the archetype ids in index order (the base among them). */
export function parseBranches(root: Json, towerIds: readonly string[]): BranchesData {
  const src = root !== null && typeof root === "object" ? root.branches : undefined;
  if (src === undefined || src === null) return emptyBranches(towerIds.length);
  const a = src.ascend;
  const pacing: AscendPacing = { minLevel: Number(a.minLevel), fromStagePercent: Number(a.fromStagePercent), offerInterval: Number(a.offerInterval), endlessFromWave: Number(a.endlessFromWave ?? 3) };
  const byTower: number[][] = towerIds.map(() => []);
  const branches: BranchDef[] = [];
  const skills: SkillDef[] = [];
  (src.branches as Json[]).forEach((b, index) => {
    const towerIndex = towerIds.indexOf(String(b.tower));
    if (towerIndex < 0) throw new Error(`branch ${b.id}: unknown tower '${b.tower}'`);
    const slot = byTower[towerIndex].length;
    if (slot > 1) throw new Error(`tower ${b.tower} has more than two branches`);
    byTower[towerIndex].push(index);
    const sk = (b.skills as Json[]).map((s, k): SkillDef => ({ index: index * 2 + k, id: String(s.id), slot: k, branchIndex: index, name: String(s.name), description: String(s.description), params: params(s.params) }));
    if (sk.length !== 2) throw new Error(`branch ${b.id} needs two skills`);
    skills.push(...sk);
    branches.push({ index, id: String(b.id), towerIndex, towerId: String(b.tower), slot, name: String(b.name), identity: String(b.identity), tier4: params(b.tier4), skills: [sk[0], sk[1]] });
  });
  return { pacing, branches, skills, byTower };
}
